import type { AccountView, Clarification, ClarificationChoice, DraftOutcome } from "../api/types.js";
import { lastDigits } from "../lib/format.js";
import { SAY } from "./bank-words.js";
import { byAccountRank } from "./home-model.js";

/**
 * 04 clarification (UX spec v3 §6.4; answer route DCTA-71 ask 2).
 *
 * The question is the resolver's sentence, the options are the server's
 * `choices[]` (their labels come from bank records, never from a model), and an
 * answer goes back BY ID through `POST /v1/drafts/{id}/answer`, where the server
 * pins it and runs the same pipeline the sentence went through.
 *
 * What the client decides is deliberately small:
 *   - which question is open — the first one the server asked, one at a time;
 *     the server re-asks the rest after each answer. A question is open when it
 *     offers something to answer with: chips, a figure, or words. DCTA-136 (phrase
 *     map F19): DCTA-135 marks every question about a field `accepts_words` (and the
 *     model path's stepless one), so the customer's next words are its answer, by
 *     its id, never a new, context-free message;
 *   - whether a typed or spoken answer names exactly ONE offered option, or is a
 *     plain figure when the question takes an amount. Otherwise, where the question
 *     takes words, they go as said (`words`): the resolver reads them ("fifty", "my
 *     main account", "Sarah"); where it takes none, they are `unmatched` and the
 *     question is asked again here (the bank would refuse them — never a loop of
 *     its refusals). Nothing here guesses which option words that name several mean.
 */
export type Reply =
  | { readonly kind: "choice"; readonly choiceId: string; readonly label: string }
  | { readonly kind: "amount"; readonly value: string; readonly label: string }
  /** DCTA-124 (F6): the customer's words, as said, for a question that takes words (R-U1). */
  | { readonly kind: "words"; readonly words: string; readonly label: string }
  | { readonly kind: "unmatched" };

/**
 * The question the customer can answer now, or none: the first the bank asked by id that offers chips, a figure or
 * words. DCTA-136 (F19): the bank's own `accepts_words` decides whether typed words are an answer — DCTA-135 sets it on
 * every question about a field, so "My main account" answers "Which account should I use…?"; a question that takes
 * nothing the app can send (the fixture path's stepless question) is said, and the next sentence is a new request.
 */
export function answerable(outcome: DraftOutcome): Clarification | undefined {
  const first = outcome.clarifications?.[0];
  return byId(first) ? first : undefined;
}

/** A question answerable by its own id: it has one, and offers chips, a figure or words. */
function byId(q: Clarification | undefined): q is Clarification {
  return q?.question_id !== undefined && ((q.choices?.length ?? 0) > 0 || q.accepts_amount === true || q.accepts_words === true);
}

/**
 * DCTA-189 (QA DW-4): every question of this turn the customer can answer by its own id, in the bank's order — the bank
 * asks "Send fifty to John"'s two together ("Two Johns are saved. Which one?" · "And fifty — fifty dollars, or fifty
 * thousand?"), and the card draws them together. None when the first is not answerable (it is said, as before).
 */
export function answerableAll(outcome: DraftOutcome): Clarification[] {
  if (answerable(outcome) === undefined) return [];
  // A later question is drawn by its own words: one without words is left for the bank to ask on its own.
  return (outcome.clarifications ?? []).filter((q, i) => byId(q) && (i === 0 || q.question_text !== undefined));
}

/** What a customer sees of a question and answers it with — its id and draft aside. */
function shapeOf(q: Clarification): string {
  return JSON.stringify([
    q.field_path ?? "",
    q.reason_code ?? "",
    q.question_text,
    (q.choices ?? []).map((c) => [c.choice_id, c.label, c.never_paid === true]),
    q.accepts_amount === true,
    q.accepts_words === true,
    q.rule_ids ?? [],
  ]);
}

/**
 * DCTA-189: the bank asked this question again (a new draft, a new id — the other question of the card was answered) and
 * it is the SAME question: the same field, words, chips (ids and labels, in order, the never-paid marker), ways to answer
 * and rule. Only then does the card keep it where it is; anything else is a new question, drawn as one.
 */
export function sameQuestion(a: Clarification, b: Clarification): boolean {
  return shapeOf(a) === shapeOf(b);
}

/**
 * DCTA-189: which open question typed or spoken words answer — the first whose chip or figure they name (`interpret`:
 * a choice or an amount); words that name neither answer the first, as they would a question asked alone. -1: none open.
 */
export function answerTarget(text: string, questions: readonly Clarification[]): number {
  if (questions.length === 0) return -1;
  const named = questions.findIndex((q) => {
    const reply = interpret(text, q);
    return reply.kind === "choice" || reply.kind === "amount";
  });
  return named < 0 ? 0 : named;
}

/**
 * DCTA-143 — the chip a bank's choice is drawn as: its label exactly as the bank sent it (a "the usual" label with
 * " — " is the deck's two-line chip, F11), and — red-team P2 finding 2 — a payee the registry says was never paid
 * (`never_paid`, DCTA-137, offered last by the bank) carries the marker "New payee · never paid" on its second line.
 * The order is the bank's; nothing is re-sorted, dropped or re-worded here. `added_at` is not printed.
 */
export function choiceChip(c: ClarificationChoice): { readonly label: string; readonly mark?: string } {
  return c.never_paid === true ? { label: c.label, mark: SAY.newPayee } : { label: c.label };
}

/** A chip the app draws from the bank's own read (`accountChips`): its label, and the words it answers with. */
export interface ReadChip {
  readonly label: string;
  readonly words: string;
}

const ACCOUNT_FIELD = /\/(debit|to|settlement)_account_ref$/;

/** A question about one of the customer's own accounts — by the field it is about, never by its words. */
export function isAccountQuestion(q: Clarification): boolean {
  return ACCOUNT_FIELD.test(q.field_path ?? "");
}

/**
 * DCTA-136 (F19; the coordinator, from Umakhan's iPhone: "Which account should I use to send S$75 to your mum?", no
 * chips, and "My main account" lost): an account question the bank sent WITHOUT choices offers the customer's own
 * accounts, from the bank's read (`GET /v1/accounts`) in Home's order, each labelled as the bank labels a choice
 * ("Everyday Savings ···4417"). A tap answers with the account's name as the bank holds it, as words — the bank's
 * resolver reads it like typed words, so nothing is pinned or guessed here. A buy's settlement account is one of the
 * trading accounts. A question the bank gave choices keeps exactly those.
 */
export function accountChips(q: Clarification, accounts: readonly AccountView[]): ReadChip[] {
  // A chip answers with words: only a question that takes words can be answered by one (the bank refuses the rest).
  if (!isAccountQuestion(q) || q.accepts_words !== true || (q.choices?.length ?? 0) > 0) return [];
  const settlement = (q.field_path ?? "").endsWith("/settlement_account_ref");
  return byAccountRank(accounts)
    .filter((a) => !settlement || a.type.includes("investment"))
    .map((a) => ({ label: `${a.display_name} ${lastDigits(a.masked_number)}`, words: a.display_name }));
}

const FIGURE = /^(?:s\$|sgd|\$)?\s*((?:[1-9]\d{0,2}(?:,\d{3})+)|\d{1,13})(?:\.(\d{1,2}))?\s*(?:dollars?|sgd)?$/i;

/** "250", "S$1,250.50", "$50", "50 dollars" → the contract's money string; anything else → undefined. */
export function parseAmount(text: string): string | undefined {
  const m = FIGURE.exec(text.trim());
  if (m === null) return undefined;
  const whole = (m[1] ?? "").replace(/,/g, "").replace(/^0+(?=\d)/, "");
  const cents = (m[2] ?? "").padEnd(2, "0");
  if (whole.length > 13) return undefined;
  if (/^0*$/.test(whole) && /^0*$/.test(cents)) return undefined;
  return `${whole === "" ? "0" : whole}.${cents}`;
}

/** A yes (§F.2 16d, F15 adds ok, okay, sure, go ahead): it picks a question's or an offer's ONE chip. */
const AFFIRM = /^(yes|yeah|yep|yup|correct|right|that'?s right|yes please|that one|ok|okay|sure|go ahead)[.!]?$/i;
/** A decline on its own (§F.2 16d, F15): it ends any open question or offer — "Nothing was sent." — but the near miss (15i). */
const DECLINE = /^(no|no thanks|no thank you|nope|not now|don'?t|don'?t send|cancel|never mind|nevermind)[.!]?$/i;

export function declines(text: string): boolean {
  return DECLINE.test(text.trim());
}

/** §F.2 16g (F13): a question answered in words or a figure — the field asks for the answer. An offer takes neither. */
export function takesWords(q: Clarification): boolean {
  return q.accepts_words === true || q.accepts_amount === true;
}

/**
 * F42 (QA D6, §F.2 23a): "A different amount" stands only beside figures to pick (15g, 15l, 17d). Under the amount question
 * it opens ("How much should go to Mum?", 17c, 16n) there is no chip: the field is the answer (16g's placeholder).
 */
export function differentAmount(q: Clarification): boolean {
  return q.accepts_amount === true && (q.choices ?? []).length > 0;
}

/** §F.2 16h (F14): words that name no chip — how to go ahead with the one chip, or which one of several. */
export function reaskLine(labels: readonly string[]): string {
  const only = labels.length === 1 ? labels[0] : undefined;
  return only !== undefined ? `To go ahead, tap \u201c${only}\u201d or say yes.` : "I'm not sure which you meant. Tap one, or say which one.";
}

/** §F.2 16i (F14): the same again — the app lets the question or the offer go. */
export const LET_GO = "I'm still not sure what you meant. Say it again to start over.";
/** §F.2 16k (F14): the question asked again was refused again — after the bank's sentence, the app's own line. */
export const START_OVER = "Say it again to start over.";

/**
 * Words while an offer (chips used once) is open and no question is (§F.2 16d, 16h). DCTA-226 (§5.7): `question` — words
 * that ask something go to the bank's offer first (it answers a question about the bank or the product there and keeps
 * the offer open); `otherwise` is what they are when the bank does not answer them there — as they were before.
 */
export type OfferReply =
  | { readonly kind: "pick"; readonly index: number }
  | { readonly kind: "decline" }
  | { readonly kind: "request" }
  | { readonly kind: "unmatched" }
  | { readonly kind: "question"; readonly otherwise: "request" | "unmatched" };

/**
 * QA D2 (DCTA-170): a new errand typed while an offer's chips are open goes to the bank, whatever its first word — a figure,
 * a currency, PayNow, a bill, someone who "needs" money. Before, only a request verb at the start counted, and "PayNow S$20
 * to 9888 4102" was answered here with 16h's re-ask: nothing reached the bank. Words with none of these signs ("that one",
 * "hmm") are still asked about.
 */
const ERRAND = /\d|\$|\bsgd\b|\b(paynow|pay|send|transfer|move|buy|sell|invest|top\s*up|bills?|needs?|owes?|give|lend|withdraw|deposit)\b/i;
/** A number that only points at a chip ("2", "option 2", "the 2nd one", "#2"): about the chips, so 16h asks which. */
const POINTER = /^(?:(?:the|option|number|no\.?|choice)\s*)?#?\d{1,2}(?:st|nd|rd|th)?(?:\s*one)?[.!?]?$/i;

/**
 * DCTA-226 (§5.7): words that ask — a question mark, or a question's first word (a spoken question has no mark), after a
 * filler or two ("so how will you buy the shares"). The bank decides what it answers at an offer; this only decides what is
 * worth asking it.
 */
const ASKS = /\?\s*$|^(?:(?:so|and|but|ok|okay|well|um|uh|hmm)[,]?\s+){0,2}(?:how|what|what's|whats|which|who|whom|whose|why|where|when|is|are|am|was|were|do|does|did|can|could|will|would|should|shall|may|might|tell\s+me|explain)\b/i;

export function offerReply(text: string, labels: readonly string[]): OfferReply {
  const said = text.trim();
  const exact = labels.findIndex((l) => squash(l) === squash(said));
  if (exact >= 0) return { kind: "pick", index: exact };
  if (AFFIRM.test(said)) return labels.length === 1 ? { kind: "pick", index: 0 } : { kind: "unmatched" };
  if (declines(said)) return { kind: "decline" };
  if (POINTER.test(said)) return { kind: "unmatched" };
  const otherwise = newRequest(said) || ERRAND.test(said) ? "request" : "unmatched";
  // MUTATION TARGET (DCTA-226): without it "Is this safe?" at an offer is asked about here and never reaches the bank.
  if (ASKS.test(said)) return { kind: "question", otherwise };
  return { kind: otherwise };
}

/** Words that can tell options apart: names (2+ letters) and reference digits (3+). */
function words(text: string): string[] {
  return text
    .toLowerCase()
    .split(/[^\p{L}\p{N}]+/u)
    .filter((w) => (/^\d+$/.test(w) ? w.length >= 3 : w.length >= 2));
}

const squash = (s: string): string => s.toLowerCase().replace(/\s+/g, " ").trim();

/** Words that point at nothing by themselves — they neither name an option nor rule one out (rule 4). */
const FILLER: ReadonlySet<string> = new Set(
  "the a an one ones my me mine please pls plz that this it its to for from with ending ends end in on of ok okay i use using want like pick choose go".split(" "),
);

export function interpret(text: string, question: Clarification): Reply {
  const said = text.trim();
  if (said === "") return { kind: "unmatched" };
  const choices = question.choices ?? [];

  // 1. The option's own words, as offered.
  const exact = choices.find((c) => squash(c.label) === squash(said));
  if (exact !== undefined) return { kind: "choice", choiceId: exact.choice_id, label: exact.label };

  // 2. A plain figure, where a figure is an answer.
  if (question.accepts_amount === true) {
    const value = parseAmount(said);
    if (value !== undefined) return { kind: "amount", value, label: said };
  }

  // 3. "Yes" to a question that offers exactly one thing.
  if (choices.length === 1 && AFFIRM.test(said)) {
    const only = choices[0];
    if (only !== undefined) return { kind: "choice", choiceId: only.choice_id, label: only.label };
  }

  // 4. Every meaningful word of the answer names exactly one option ("the Doe one", "ending 8892", "Joint account").
  //    DCTA-136: a word that names NO option means the app cannot be sure — "My main account" is not "Joint Account"
  //    because "account" is on that chip; it goes to the bank as words (where the question takes them), which knows the
  //    primary account. Fillers ("the", "one", "my", "ending") carry no meaning here.
  const offered = new Set(choices.flatMap((c) => words(c.label)));
  const meaningful = words(said).filter((w) => !FILLER.has(w));
  const fits =
    meaningful.length > 0 && meaningful.every((w) => offered.has(w))
      ? choices.filter((c) => {
          const own = new Set(words(c.label));
          return meaningful.every((w) => own.has(w));
        })
      : [];
  const one = fits.length === 1 ? fits[0] : undefined;
  if (one !== undefined) return { kind: "choice", choiceId: one.choice_id, label: one.label };

  // 5. DCTA-124 (F6, §F.2 8d/8e) for R-U1, DCTA-135/136 (F19) for every question about a field: the words, as said —
  //    a saved payee's name, a PayNow number, an account, a figure in words. The bank reads them (its firewall first);
  //    nothing here parses a name or a number, and words that fit several options are the bank's to ask about.
  if (question.accepts_words === true) return { kind: "words", words: said, label: said };
  return { kind: "unmatched" };
}

/**
 * DCTA-84 A2 — with a question open, are these words an answer or a new request? A
 * protective request (freeze, lock, block, stop, a scam) always wins: it is new, whatever
 * it touches. Words that START a request (a verb of the bank, a question word) are new
 * unless they name one of the options ("Pay the minimum" answers "Which amount?"). A
 * fragment ("the minimum", "fifty") stays an answer, asked again if it names nothing (04).
 * The M1 stand-in's rule: in production the drafting model classifies it (README ask hh).
 */
const PROTECTIVE = /\b(freeze|frozen|lock|block|stop|scam|scammed|stolen|lost|report)\b/i;
// DCTA-124: "add" too — "add Priya as a payee" is a request (the bank answers it with its pointer to
// Payees, §F.2 5a), never the name of a payee given to an R-U1 question.
const STARTS_REQUEST = /^\s*(please\s+)?(send|pay|transfer|move|buy|sell|invest|put|top\s*up|show|cancel|unlock|raise|lower|add|what|what's|whats|how|when|where|i\s+want|i'd\s+like|can\s+you)\b/i;

export function newRequest(text: string, namesOption = false): boolean {
  if (PROTECTIVE.test(text)) return true;
  return !namesOption && STARTS_REQUEST.test(text);
}
